import * as React from "react";
import { Alert, Text } from "@uikit";
import {
  GolfOrderLine,
  GolfOrderLineCart_Types,
  GolfOrderLineGolferInfo_Player,
} from "@kalabo/golfsavers-common";
import {
  TransferOrderDetails_AdditionalInfo_AreaPickup,
  TransferOrderDetails_AdditionalInfo_GolfPickup,
  TransferOrderLine,
} from "@kalabo/golfsavers-common";
import dayjs from "dayjs";

import { IoTrash } from "@react-icons/all-files/io5/IoTrash";
import XE from "@components/common/xe";
import CartItemPriceBreakdown from "../cartItemPriceBreakdown";
import { ChevronUpIcon } from "@heroicons/react/outline";
import { Hole_Option } from "@kalabo/golfsavers-common";
import _ from "lodash";
import { capitalise } from "@kalabo/golfsavers-common";

interface CartItemProps {
  golf_line: GolfOrderLine;
  transfer_line: TransferOrderLine;
  display_currency: string;
  showHeading?: boolean;
  summaryMode?: boolean;
  showTotal?: boolean;
  footer?: React.ReactNode;
  showBreakdown?: boolean;
  showSeparator?: boolean;
  showImage?: boolean;
  note?: string;
  onDelete?: () => void;
}

const CartItem = ({
  golf_line,
  transfer_line,
  showHeading = true,
  summaryMode = false,
  display_currency,
  showTotal = true,
  showBreakdown = true,
  showSeparator = true,
  footer,
  showImage = true,
  note,
  onDelete,
}: CartItemProps) => {
  const [showBreakdownDetails, setShowBreakdownDetails] = React.useState(false);

  const showGolferInfo = React.useMemo(() => {
    if (golf_line?.course?.policies?.identity_requirement?.enabled !== "YES")
      return false;
    return true;
  }, []);

  const calculateTotalValue = () => {
    let val = 0;

    // Green Fee
    val =
      (golf_line?.green_fee?.cost + golf_line?.green_fee?.margin) *
        golf_line?.where_when_who?.number_of_golfers +
      Number(
        golf_line?.where_when_who.number_of_golfers % 2
          ? golf_line?.green_fee?.surcharge || 0
          : 0
      );

    // Caddies
    if (
      golf_line?.caddies?.enabled &&
      golf_line?.caddies?.payment_option !== null &&
      golf_line?.caddies?.payment_option !== "ON_ARRIVAL"
    ) {
      val += golf_line?.caddies?.total;
    }

    // Carts
    if (
      golf_line?.carts?.enabled &&
      golf_line?.carts?.payment_option !== null &&
      golf_line?.carts?.payment_option !== "ON_ARRIVAL"
    ) {
      golf_line?.carts?.types?.map(
        // eslint-disable-next-line array-callback-return
        (cart_type: GolfOrderLineCart_Types, idx: any) => {
          val += golf_line?.carts?.types[idx]?.total;
        }
      );
    }

    if (transfer_line?.vehicle?.vehicle?.id) {
      val += transfer_line?.vehicle?.total * transfer_line?.vehicle?.quantity;
    }

    return val;
  };

  const teeTime = React.useMemo(() => {
    return (
      <>
        {" "}
        {golf_line?.tee_time?.start_time} and {golf_line?.tee_time?.end_time}{" "}
      </>
    );
  }, [golf_line?.tee_time]);

  const calculateCaddies = () => {
    let val = 0;

    // Caddies
    if (
      golf_line?.caddies?.enabled &&
      golf_line?.caddies?.payment_option !== null &&
      golf_line?.caddies?.payment_option !== "ON_ARRIVAL"
    ) {
      val += golf_line?.caddies?.total;
    }

    return val;
  };

  const getCartTotal = () => {
    let val = 0;
    golf_line?.carts?.types?.map(
      // eslint-disable-next-line array-callback-return
      (cart_type: GolfOrderLineCart_Types, idx: any) => {
        val += golf_line?.carts?.types[idx]?.total;
      }
    );
    return val;
  };

  const calculateCarts = () => {
    let val = 0;

    // Carts
    if (
      golf_line?.carts?.enabled &&
      golf_line?.carts?.payment_option !== null &&
      golf_line?.carts?.payment_option !== "ON_ARRIVAL"
    ) {
      val = getCartTotal();
    }

    return val;
  };

  const missingGolferInfo = React.useMemo(() => {
    let missing = false;
    _.times(golf_line?.where_when_who?.number_of_golfers, (idx) => {
      let playerInfo = golf_line?.golfer_info?.players[idx];
      if (!playerInfo) {
        missing = true;
        return;
      }

      if (playerInfo?.full_name === "") {
        missing = true;
        return;
      }

      if (
        golf_line?.course?.policies?.identity_requirement?.nationality_required
      ) {
        if (playerInfo?.nationality === "" || !playerInfo?.nationality) {
          missing = true;
          return;
        }
      }
      if (
        golf_line?.course?.policies?.identity_requirement?.passport_required
      ) {
        if (
          playerInfo?.passport_number === "" ||
          !playerInfo?.passport_number
        ) {
          missing = true;
          return;
        }
      }
    });

    return missing;
  }, [golf_line]);

  const holeOptions = React.useMemo(() => {
    if (golf_line?.hole_options?.selected_option !== "ANY") {
      let hole_option = golf_line?.hole_options?.hole_options?.find(
        (option: Hole_Option) => {
          return option.id === golf_line?.hole_options?.selected_option;
        }
      );
      if (hole_option) {
        return `(${hole_option.description}) `;
      }
    }
    return null;
  }, [golf_line?.hole_options?.selected_option]);

  if (summaryMode) {
    return (
      <>
        <div className="flex gap-3">
          {showImage && (
            <div className="flex">
              <div
                className="w-[80px] h-[80px] sm:w-[120px] sm:h-[120px] bg-grey2 overflow-hidden rounded-md"
                style={{
                  backgroundSize: "cover",
                  backgroundImage: `url(${process.env.GATSBY_IMAGE_PREFIX}${golf_line.course.featured_image}?aspect_ratio=1:1&width=400)`,
                }}
              ></div>
            </div>
          )}
          <div
            className={`flex gap-4 overflow-hidden ${
              showImage ? "flex-col" : "flex-row"
            } justify-center`}
          >
            <div className={`flex flex-col ${!showImage && "flex-1"}`}>
              <Text
                size={15}
                sizeDesktop={17}
                sizeTablet={17}
                leading={1.3}
                weight="semibold"
                color="black"
              >
                {golf_line?.product?.config?.holes} Holes at{" "}
                {golf_line.course?.name?.en}
              </Text>
              <Text
                size={12}
                sizeTablet={13}
                sizeDesktop={13}
                leading={1.5}
                color="black60"
                className={`mb-0`}
              >
                {dayjs(golf_line.where_when_who.date).format(
                  "ddd, MMM D, YYYY"
                )}{" "}
                · Between
                {teeTime}
              </Text>

              <Text
                size={11}
                className="mt-[4px]"
                leading={1.3}
                color="black80"
              >
                {`${golf_line.where_when_who.number_of_golfers} x Green Fee ${
                  holeOptions ? holeOptions : ""
                }`}

                {(golf_line?.caddies?.enabled || golf_line?.carts?.enabled) && (
                  <>
                    {" + "}
                    {golf_line?.caddies?.enabled &&
                      `${golf_line.where_when_who.number_of_golfers} x ${
                        golf_line?.caddies?.type === "SINGLE_CADDIE"
                          ? "Single Caddies"
                          : "Shared Caddies"
                      } ${calculateCaddies() <= 0 ? " (Pay at Course)" : ""}`}
                    {golf_line?.carts?.enabled && (
                      <>
                        {" + "}
                        {golf_line?.carts?.types.map(
                          (type: GolfOrderLineCart_Types) => {
                            if (type.quantity === 0) return null;
                            return (
                              <>
                                {type.quantity} x{" "}
                                {type.type === "SINGLE_CART"
                                  ? "Single Cart"
                                  : "Shared Cart"}
                              </>
                            );
                          }
                        )}
                        {calculateCarts() <= 0 ? " (Pay at Course)" : ""}
                      </>
                    )}
                  </>
                )}
                {transfer_line?.vehicle?.vehicle?.id && <>+ Transfers</>}
              </Text>
            </div>
            <div className="flex flex-col gap-1">
              <Text
                size={14}
                sizeDesktop={15}
                sizeTablet={15}
                weight="semibold"
                color="black"
                className={`mb-0 ${!showImage && "text-right"}`}
              >
                <XE
                  base_currency={golf_line?.supplier?.currency}
                  currency={display_currency}
                  value={calculateTotalValue()}
                />
              </Text>
              <Text
                size={11}
                color="black60"
                className={`mb-0 ${!showImage && "text-right"}`}
              >
                {display_currency !== golf_line?.supplier?.currency && (
                  <>
                    <XE
                      base_currency={golf_line?.supplier?.currency}
                      currency={golf_line?.supplier?.currency}
                      value={calculateTotalValue()}
                    />
                  </>
                )}
              </Text>
            </div>
          </div>
        </div>
        {showSeparator && <div className="border-b border-grey3"></div>}
      </>
    );
  }

  const getAreaPickupDetails = () => {
    if (transfer_line?.transfer_legs?.outbound?.from_type !== "Area")
      return null;
    const config = transfer_line.transfer_legs.outbound
      .from_details as TransferOrderDetails_AdditionalInfo_AreaPickup;

    return config?.address ? ` (${config.address})` : " (hotel or residence)";
  };

  const getGolfArrivalTime = () => {
    const config = transfer_line.transfer_legs.outbound
      .to_details as TransferOrderDetails_AdditionalInfo_GolfPickup;

    return (
      <Text size={14} bullet leading={1.5} color="black80">
        Arrival: {config.requested_arrival_time} minutes before tee time
      </Text>
    );
  };

  const additionalNotes = () => {
    return (
      <>
        {golf_line.green_fee.rate_note && (
          <div className="border-b border-grey3 pb-5">
            <Text
              size={15}
              leading={1.3}
              weight="semibold"
              color="black90"
              className="mb-2"
            >
              Additional Notes
            </Text>
            <Text size={14} bullet leading={1.5} color="black80">
              {golf_line.green_fee.rate_note}
            </Text>
          </div>
        )}
      </>
    );
  };

  const hasIncludedExtras = () => {
    if (
      golf_line.product.config.caddie ||
      golf_line.product.config.shared_cart ||
      golf_line.product.config.single_cart ||
      golf_line.product.config.shared_caddie
    ) {
      return true;
    }
    return false;
  };

  const hasExtras = () => {
    if (hasIncludedExtras()) {
      return true;
    }

    if (golf_line.carts.enabled) {
      return true;
    }

    if (golf_line.caddies.enabled) {
      return true;
    }
  };

  const includedExtras = () => {
    if (!hasIncludedExtras()) return null;
    return (
      <>
        <div className="border-b border-grey3 pb-5">
          <Text
            size={15}
            leading={1.3}
            weight="semibold"
            color="black90"
            className="mb-2"
          >
            Included Extras
          </Text>
          {golf_line.product.config.caddie && (
            <Text size={14} bullet leading={1.5} color="black80">
              {golf_line.caddies?.quantity} x{" "}
              {golf_line?.caddies?.type === "SINGLE_CADDIE"
                ? "single caddie"
                : "shared caddies"}
            </Text>
          )}
          {(golf_line.product.config.shared_cart ||
            golf_line.product.config.single_cart) && (
            <>
              {golf_line?.carts?.types.map((type: GolfOrderLineCart_Types) => {
                if (type.quantity === 0) return null;
                return (
                  <Text size={14} bullet leading={1.5} color="black80">
                    {type.quantity} x{" "}
                    {type.type === "SINGLE_CART"
                      ? "single cart"
                      : "shared cart"}
                  </Text>
                );
              })}
            </>
          )}
        </div>
      </>
    );
  };

  const hasPrepaidExtras = () => {
    if (
      golf_line?.caddies?.enabled &&
      golf_line?.caddies?.payment_option !== "ON_ARRIVAL"
    ) {
      return true;
    }

    if (
      golf_line?.carts?.enabled &&
      golf_line?.carts?.payment_option !== "ON_ARRIVAL"
    ) {
      return true;
    }

    return false;
  };

  const hasPayAtCourseExtras = () => {
    if (
      golf_line?.caddies?.enabled &&
      golf_line?.caddies?.payment_option === "ON_ARRIVAL"
    ) {
      return true;
    }

    if (
      golf_line?.carts?.enabled &&
      golf_line?.carts?.payment_option === "ON_ARRIVAL"
    ) {
      return true;
    }

    return false;
  };

  const prepaidExtras = () => {
    if (!hasPrepaidExtras()) {
      return null;
    }
    return (
      <div className="border-b border-grey3 pb-5">
        <Text
          size={15}
          leading={1.3}
          weight="semibold"
          color="black90"
          className="mb-2"
        >
          Prepaid Extras
        </Text>

        {golf_line?.caddies?.enabled &&
          golf_line?.caddies?.payment_option !== "ON_ARRIVAL" && (
            <Text size={14} bullet leading={1.5} color="black80">
              {golf_line.caddies?.quantity} x{" "}
              {golf_line?.caddies?.type === "SINGLE_CADDIE"
                ? "single caddie"
                : "shared caddies"}
            </Text>
          )}

        {golf_line?.carts?.enabled &&
          golf_line?.carts?.payment_option !== "ON_ARRIVAL" && (
            <>
              {golf_line?.carts?.types.map((type: GolfOrderLineCart_Types) => {
                if (type.quantity === 0) return null;
                return (
                  <Text size={14} bullet leading={1.5} color="black80">
                    {type.quantity} x{" "}
                    {type.type === "SINGLE_CART"
                      ? "single cart"
                      : "shared cart"}
                  </Text>
                );
              })}
            </>
          )}
      </div>
    );
  };

  const payAtCourseExtras = () => {
    if (!hasPayAtCourseExtras()) {
      return null;
    }
    return (
      <div className="border-b border-grey3 pb-5">
        <Text
          size={15}
          leading={1.3}
          weight="semibold"
          color="black90"
          className="mb-2"
        >
          Pay-at-course extras
        </Text>

        {golf_line?.caddies?.enabled &&
          golf_line?.caddies?.payment_option === "ON_ARRIVAL" && (
            <Text size={14} bullet leading={1.5} color="black80">
              {golf_line.caddies?.quantity} x{" "}
              {golf_line?.caddies?.type === "SINGLE_CADDIE"
                ? "single caddie"
                : "shared caddies"}
              {" - "}
              <XE
                comparison={true}
                base_currency={golf_line?.supplier?.currency}
                currency={display_currency}
                value={golf_line?.caddies?.total}
              />{" "}
              due on arrival
            </Text>
          )}

        {golf_line?.carts?.enabled &&
          golf_line?.carts?.payment_option === "ON_ARRIVAL" && (
            <>
              {golf_line?.carts?.types.map((type: GolfOrderLineCart_Types) => {
                if (type.quantity === 0) return null;
                return (
                  <Text size={14} bullet leading={1.5} color="black80">
                    {type.quantity} x{" "}
                    {type.type === "SINGLE_CART"
                      ? "single cart"
                      : "shared cart"}
                    {" - "}
                    <XE
                      comparison={true}
                      base_currency={golf_line?.supplier?.currency}
                      currency={display_currency}
                      value={getCartTotal()}
                    />{" "}
                    due on arrival
                  </Text>
                );
              })}
            </>
          )}

        {(calculateCarts() <= 0 || calculateCaddies() <= 0) && (
          <div className="pt-2">
            <Alert
              spacing="micro"
              iconAlign="center"
              title={
                <>You must pay the golf course directly for the items above</>
              }
              type="warning"
              className="m-0"
            />
          </div>
        )}
      </div>
    );
  };

  const displayExtras = () => {
    if (!hasExtras()) return null;
    return (
      <>
        {includedExtras()}
        {prepaidExtras()}
        {payAtCourseExtras()}
        {additionalNotes()}
      </>
    );
  };

  return (
    <>
      <div className="border border-grey3 rounded-lg">
        <div className="flex flex-col gap-5 px-3 py-3">
          {showHeading && (
            <div className="mb-0 pb-3 flex items-center border-b border-grey2">
              <div className="flex  items-center flex-1">
                {showImage && (
                  <div
                    className="flex w-[75px] sm:w-[100px] !h-[75px] bg-grey2 rounded-[2px] overflow-hidden mr-3"
                    style={{
                      backgroundSize: "cover",
                      backgroundImage: `url(${process.env.GATSBY_IMAGE_PREFIX}${golf_line.course.featured_image}?aspect_ratio=5:4&width=400)`,
                    }}
                  ></div>
                )}

                <div className="flex-1 flex flex-col">
                  <Text
                    sizeDesktop={17}
                    sizeTablet={17}
                    size={15}
                    leading={1.3}
                    weight="semibold"
                    color="black90"
                    className="mb-0"
                  >
                    {golf_line.course?.name?.en}
                  </Text>

                  <Text size={14} leading={1.5} color="black80">
                    {dayjs(golf_line.where_when_who.date).format(
                      "ddd, DD MMM YYYY"
                    )}
                  </Text>
                  {/* 
                  <Text size={14} leading={1.5} color="black80">
                    {golf_line.course.region}, {golf_line.course.country}
                  </Text>*/}
                </div>
              </div>
              <div
                onClick={() => {
                  onDelete();
                }}
                className="p-2 ml-3 bg-grey border  cursor-pointer border-grey3 rounded-lg"
              >
                <IoTrash className="w-6 h-6 text-red" />
              </div>
            </div>
          )}
          <div className="border-b border-grey3 pb-5">
            <Text
              size={15}
              leading={1.3}
              weight="semibold"
              color="black90"
              className="mb-2"
            >
              Your golf
            </Text>

            <Text size={14} bullet leading={1.5} color="black80">
              {`${golf_line?.product?.config?.holes} Holes at ${golf_line?.course?.name?.en} `}
            </Text>

            <Text size={14} bullet leading={1.5} color="black80">
              {golf_line.where_when_who.number_of_golfers} players
            </Text>
            <Text size={14} bullet leading={1.5} color="black80">
              {dayjs(golf_line.where_when_who.date).format("ddd, DD MMM YYYY")}
            </Text>
            <Text size={14} bullet leading={1.5} color="black80">
              Tee time between {teeTime}
            </Text>
          </div>
          {displayExtras()}
          {transfer_line?.vehicle?.vehicle?.id && (
            <>
              <div className="border-b border-grey3 pb-5">
                <Text
                  size={15}
                  leading={1.3}
                  weight="semibold"
                  color="black90"
                  className="mb-2"
                >
                  Transfers
                </Text>
                {transfer_line.where_when_who.type === "CUSTOM" ? (
                  <>
                    <Text size={14} bullet leading={1.5} color="black80">
                      Pick up:{" "}
                      {transfer_line?.transfer_legs?.outbound?.from_name}
                      {getAreaPickupDetails()}
                    </Text>
                    {getGolfArrivalTime()}
                    <Text size={14} bullet leading={1.5} color="black80">
                      Drop off: {transfer_line?.transfer_legs?.inbound?.to_name}
                    </Text>
                    <Text size={14} bullet leading={1.5} color="black80">
                      {transfer_line?.vehicle?.vehicle?.name.en} (up to{" "}
                      {transfer_line?.vehicle?.max_pax} pax)
                    </Text>
                  </>
                ) : (
                  <>
                    <Text size={14} bullet leading={1.5} color="black80">
                      Pick-up/drop-off:{" "}
                      {transfer_line?.transfer_legs?.outbound?.from_name}
                      {getAreaPickupDetails()}
                    </Text>
                    {getGolfArrivalTime()}
                    <Text size={14} bullet leading={1.5} color="black80">
                      {transfer_line?.vehicle?.vehicle?.name.en} (up to{" "}
                      {transfer_line?.vehicle?.max_pax} pax)
                    </Text>
                  </>
                )}
              </div>
            </>
          )}
          {showGolferInfo && (
            <div className="border-b border-grey3 pb-5">
              <Text
                size={15}
                leading={1.3}
                weight="semibold"
                color="black80"
                className="mb-2"
              >
                Golfer information
              </Text>
              {_.times(golf_line?.where_when_who?.number_of_golfers, (i) => {
                let player = golf_line?.golfer_info?.players[
                  i
                ] as GolfOrderLineGolferInfo_Player;
                return (
                  <Text size={14} bullet leading={1.5} color="black80">
                    Player {i + 1}:{" "}
                    {player
                      ? `${player.full_name ? player.full_name : "Name TBC"}
                      ${
                        player.nationality
                          ? ` / ${capitalise(player.nationality)}`
                          : `/ Nationality TBC`
                      }
                      ${
                        golf_line?.course?.policies?.identity_requirement
                          ?.passport_required
                          ? player?.passport_number
                            ? ` / ${player?.passport_number}`
                            : " / Passport No. TBC"
                          : ""
                      }`
                      : "TBC"}
                  </Text>
                );
              })}
              {missingGolferInfo && (
                <div className="py-2">
                  <Alert
                    className="py-[10px]"
                    type="warning"
                    title="Information for all golfers will be required before final confirmation of your booking"
                  ></Alert>
                </div>
              )}
            </div>
          )}
          {note && (
            <div className="border-b border-grey3 pb-5">
              <Text
                size={15}
                leading={1.3}
                weight="semibold"
                color="black90"
                className="mb-2"
              >
                Special request
              </Text>
              <Text size={14} bullet leading={1.5} color="black80">
                {note}
              </Text>
            </div>
          )}
          {showTotal && (
            <div className="flex flex-col gap-3">
              <div className="flex justify-between">
                <div className="flex flex-col gap-1">
                  <Text
                    size={17}
                    weight="semibold"
                    color="black"
                    className="mb-0"
                  >
                    Total
                  </Text>
                  <Text size={14} color="black60" className="mb-0">
                    for {golf_line?.where_when_who?.number_of_golfers} golfers
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text
                    size={17}
                    weight="semibold"
                    color="black"
                    className="mb-0 text-right"
                  >
                    <XE
                      base_currency={golf_line?.supplier?.currency}
                      currency={display_currency}
                      value={calculateTotalValue()}
                    />
                  </Text>
                  <Text size={14} color="black60" className="mb-0 text-right">
                    {display_currency !== golf_line?.supplier?.currency && (
                      <>
                        <XE
                          base_currency={golf_line?.supplier?.currency}
                          currency={golf_line?.supplier?.currency}
                          value={calculateTotalValue()}
                        />
                      </>
                    )}
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>
        {showBreakdown && (
          <>
            {showBreakdownDetails && (
              <div className="flex flex-col gap-3 px-3 py-3 bg-grey">
                <CartItemPriceBreakdown
                  golf_line={golf_line}
                  transfer_line={transfer_line}
                  display_currency={display_currency}
                />
              </div>
            )}
            <div
              className="text-center text-13 leading-[133%] font-semibold cursor-pointer hover:underline  px-3 py-2 bg-grey text-blue border-t border-grey3"
              onClick={() => setShowBreakdownDetails(!showBreakdownDetails)}
            >
              {showBreakdownDetails ? "Hide" : "Show"} price breakdown{" "}
              <ChevronUpIcon
                className={`h-4 w-4 inline-block ${
                  !showBreakdownDetails && "rotate-180"
                }`}
              />
            </div>
          </>
        )}
        {footer}
      </div>
    </>
  );
};

export default CartItem;
